import { fetchWithSession } from '../../Api/BaseApi';
import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import { toast } from 'react-toastify';
import { creators as DefaultActions } from '../../Reducers/Reporting/Default';

import GeneralLedgerTable from './generalledgertable';
import EmployeeTable from './employeetable';
import EmployeeHolidayTable from './employeeholiday';
import ClientTable from './clienttable';
import OpportunityTable from './opportunity';
import ProjectTable from './project';
import ProjectPerformanceSummaryTable from './projectperformancesummary';
import BalanceSheetTable from './balancesheet';
import IncomeStatement from './incomestatement';
import AccountsReceivableInvoiceTable from './accountsreceivableinvoice';
import CashFlowTable from './cashflow';
import FinancialMetricTable from './financialmetric';
import EmployeeForecastTable from './employeeforecast';
import EmployeeUtilisationTable from './employeeutilisation';
import OpportunityPerformanceTable from './opportunityperformance';
import WorkGeneratedTable from './workgenerated';
import KPIResultTable from './kpiresult';
import NewRecordsConfirmModal from './components/NewRecordsConfirmModal';
import xero_logo from '../../Assets/image/xero.png';
import myob_logo from '../../Assets/image/myob.png';
import sql_logo from '../../Assets/image/sql1.png';
import rapport_logo from '../../Assets/image/rapport1.png';
import workflow_max_logo from '../../Assets/image/workflow_max1.png';
import manual_entry_logo from '../../Assets/image/manual-icon.jpg';
import synergy_logo from '../../Assets/image/synergy.png';
import deltek_logo from '../../Assets/image/deltek_vp.png';
import simplify_logo from '../../Assets/image/simplify.png';
import ProgressButton from '../../Components/ProgressButton';
import appdata from '../../Services/appdata';
import ManageReportDataApi from '../../Api/ApiManageReportData';
import {
  xeroAccountLists,
  sqlAccountLists,
  myobAccountLists,
  rapportAccountLists,
  workflowAccountLists,
  manaualEntryAccountLists,
  synergyAccountLists,
  deltekVpAccountLists,
  simplifyAccountLists,
  reportDataTableNames,
} from '../../Utils/constants';
import './style.css';

const tableCompNames = [
  GeneralLedgerTable,
  EmployeeTable,
  EmployeeHolidayTable,
  ClientTable,
  OpportunityTable,
  ProjectTable,
  ProjectPerformanceSummaryTable,
  BalanceSheetTable,
  IncomeStatement,
  AccountsReceivableInvoiceTable,
  CashFlowTable,
  FinancialMetricTable,
  EmployeeUtilisationTable,
  EmployeeForecastTable,
  OpportunityPerformanceTable,
  WorkGeneratedTable,
  KPIResultTable,
];

function buildTableNamesAndComps(apiData) {
  let getTableNames = [];
  let filterTableNames = [];
  let tableComp_names = [];
  if (apiData.length > 0) {
    apiData.forEach((data) => {
      if (data.ApiAccounts && JSON.parse(data.ApiAccounts).length > 0)
        JSON.parse(data.ApiAccounts).forEach((account) => getTableNames.push(account));
    });
  }
  reportDataTableNames.forEach((table, index) => {
    if (getTableNames.indexOf(table) !== -1) {
      filterTableNames.push(table);
      tableComp_names.push(tableCompNames[index]);
    }
  });
  // Insert special tables if needed
  let summaryFlag = false, forecastFlag = false;
  for (let i = 0; i < filterTableNames.length; i++) {
    if (filterTableNames[i] === 'Project') {
      if (!filterTableNames.find((name) => name === 'Project Performance Summary')) {
        filterTableNames.splice(i + 1, 0, 'Project Performance Summary');
        tableComp_names.splice(i + 1, 0, ProjectPerformanceSummaryTable);
        summaryFlag = true;
      }
    }
    if (filterTableNames[i] === 'Employee') {
      if (!filterTableNames.find((name) => name === 'Employee Holiday')) {
        filterTableNames.splice(i + 1, 0, 'Employee Holiday');
        tableComp_names.splice(i + 1, 0, EmployeeHolidayTable);
      }
    }
    if (filterTableNames[i] === 'Employee Utilisation') {
      if (!filterTableNames.find((name) => name === 'Employee Forecast')) {
        filterTableNames.splice(i + 1, 0, 'Employee Forecast');
        tableComp_names.splice(i + 1, 0, EmployeeForecastTable);
        forecastFlag = true;
      }
    }
  }
  if (filterTableNames.indexOf('Opportunity Performance') === -1) {
    filterTableNames.push('Opportunity Performance');
    tableComp_names.push(OpportunityPerformanceTable);
  }
  return { filterTableNames, tableComp_names, summaryFlag, forecastFlag };
}

const apiTypeLogos = {
  Xero: xero_logo,
  Sql: sql_logo,
  Myob: myob_logo,
  Rapport: rapport_logo,
  'Workflow Max': workflow_max_logo,
  'Manual Entry': manual_entry_logo,
  Synergy: synergy_logo,
  Deltek: deltek_logo,
  Simplify: simplify_logo,
};

function ApiTableRow({ tableName, selectedApis, selectedTableName, refreshLoading, handleTemplateSelect, handleTemplateRefresh }) {
  const tableApis = selectedApis.filter((api) => {
    try {
      return api.ApiAccounts && JSON.parse(api.ApiAccounts).indexOf(tableName) !== -1;
    } catch (error) {
      return false;
    }
  });

  // Special case rows
  if ((tableName === 'Opportunity Performance' && tableApis.length === 0) || tableName === 'Employee Holiday') {
    return (
      <TableRow
        sx={{ background: selectedTableName === tableName ? 'rgba(0,0,0,0.1)' : '' }}
        style={{ height: 77 }}
      >
        <TableCell
          component="td"
          scope="row"
          align="left"
          colSpan={3}
          sx={{ cursor: 'pointer' }}
          onClick={() => handleTemplateSelect(tableName)}
        >
          {tableName}
        </TableCell>
      </TableRow>
    );
  }  
  // API rows
  let specialRendered = false;
  return selectedApis.map((selected_api, idx) => {
    if (
      selected_api.ApiAccounts &&
      JSON.parse(selected_api.ApiAccounts).length > 0 &&
      JSON.parse(selected_api.ApiAccounts).indexOf(tableName) !== -1
    ) {
      const logo = apiTypeLogos[selected_api.ApiType];
      const isSpecial = tableName === 'Project Performance Summary' || (tableName === 'Opportunity' && selected_api.ApiType !== 'Simplify');
      if (isSpecial) {
        if (specialRendered) return null;
        specialRendered = true;
      }

      return (
        <TableRow
          key={selected_api.ApiType + tableName}
          sx={{ background: selectedTableName === tableName ? 'rgba(0,0,0,0.1)' : '' }}
          style={{ height: 77 }}
        >
          <TableCell
            component="td"
            scope="row"
            align="left"
            colSpan={isSpecial ? 3 : 1}
            sx={{ cursor: 'pointer' }}
            onClick={() => handleTemplateSelect(tableName)}
          >
            {tableName}
          </TableCell>
          {!isSpecial && (
            <>
              <TableCell
                align="right"
                sx={{ width: 137, paddingRight: '2px !important', paddingLeft: '2px !important' }}
              >
                {(selected_api.ApiType === 'Synergy' && tableName === 'Employee Utilisation')
                || (selected_api.ApiType === 'Deltek' && tableName === 'Income Statement')
                || (tableName === 'Work Generated' && selected_api.ApiType !== 'Simplify') ? null : (
                  <ProgressButton
                    variant="contained"
                    color="secondary"
                    size="small"
                    onClick={(ev) => handleTemplateRefresh(ev, tableName, selected_api.ApiType)}
                    loading={selectedTableName === tableName && refreshLoading}
                  >
                    Refresh
                  </ProgressButton>
                )}
              </TableCell>
              <TableCell align="right" style={{ paddingLeft: 0, paddingRight: 0 }}>
                {logo && <img src={logo} alt={selected_api.ApiType.toLowerCase()} style={{ height: 30, margin: 4, marginBottom: 0 }} />}
              </TableCell>
            </>
          )}
        </TableRow>
      );
    }
    return null;
  });
}

function LinearProgressWithLabel(props) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Box sx={{ width: '100%', mr: 1 }}>
        <LinearProgress variant="determinate" {...props} />
      </Box>
      <Box sx={{ minWidth: 35 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {`${Math.round(props.value)}%`}
        </Typography>
      </Box>
    </Box>
  );
}

function ManageReportData() {
  const dispatch = useDispatch();
  const [filterTableNames, setFilterTableNames] = useState([]);
  const [tableCompanyNames, setTableCompanyNames] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(-1);
  const [selectedOrganisation, setSelectedOrganisation] = useState(-1);
  const [selectedDates, setSelectedDates] = useState([]);
  const [activeFilter, setActiveFilter] = useState(false);
  const [selectedTableName, setSelectedTableName] = useState('');
  const [companies, setCompanies] = useState([]);
  const [organisations, setOrganisations] = useState([]);
  const [dates, setDates] = useState([]);
  const [projects, setProjects] = useState([]);
  const [projectForecasts, setProjectForecasts] = useState([]);
  const [clients, setClients] = useState([]);
  const [projecttypes, setProjecttypes] = useState([]);
  const [projectsubtypes, setProjectsubtypes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [chartofaccounts, setChartofaccounts] = useState([]);
  const [tabledata, setTabledata] = useState([]);
  const [selectedApis, setSelectedApis] = useState([]);
  const [refreshAllLoading, setRefreshAllLoading] = useState(false);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [groupCheck, setGroupCheck] = useState(false);
  const [roleCheck, setRoleCheck] = useState(false);
  const [projectPerformanceSummaryFlag, setProjectPerformanceSummaryFlag] = useState(false);
  const [employeeForecastFlag, setEmployeeForecastFlag] = useState(false);
  const [currentRefreshCount, setCurrentRefreshCount] = useState(0);
  const [totalRefreshCount, setTotalRefreshCount] = useState(0);
  const [downloadRawSynergyExportEnabled, setDownloadRawSynergyExportEnabled] = useState(false);
  const [synergyProjectListLoading, setSynergyProjectListLoading] = useState(false);
  const hasSynergyIntegration = selectedApis.some((api) => {
    if (api.ApiType !== 'Synergy' || !api.ApiAccounts) return false;
    try {
      const accounts = JSON.parse(api.ApiAccounts);
      return Array.isArray(accounts) && accounts.length > 0;
    } catch (error) {
      return false;
    }
  });
  const [newRecordsConfirmModal, setNewRecordsConfirmModal] = useState({
    open: false,
    tableName: '',
    newRecordsCount: 0,
  });

  const refreshContinueRef = useRef(null);

  const sanitizeFilenamePart = (value) => String(value || 'unknown')
    .replace(/[^a-z0-9_-]+/gi, '_')
    .replace(/^_+|_+$/g, '') || 'unknown';

  const downloadRawSynergyExport = async (rawExports, refreshLabel = 'synergy-refresh') => {
    const exportList = Array.isArray(rawExports) ? rawExports.filter(Boolean) : [rawExports].filter(Boolean);
    if (exportList.length === 0) return;

    for (const entry of exportList) {
      try {
        const path = entry?.downloadPath;
        // Download each selected month as a compressed server-generated ZIP,
        // never as a 400MB+ JSON payload in the task status response.
        if (!path || !path.startsWith('/api/managereportdata/')) {
          throw new Error('The Synergy refresh did not supply a ZIP download link.');
        }
        const token = appdata.getItem('token');
        if (!token) throw new Error('Please sign in again to download the Synergy raw data.');
        const response = await fetchWithSession(`${window.location.origin}${path}`, {
          cache: 'no-store',
        });
        if (!response.ok) {
          throw new Error(response.status === 401
            ? 'Your R4D session expired. Sign in again to download the raw data.'
            : `ZIP download failed (HTTP ${response.status}).`);
        }
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = entry.filename || `synergy-raw-export-${sanitizeFilenamePart(refreshLabel)}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(url), 30000);
      } catch (error) {
        toast.error(`Synergy raw export download failed: ${error.message}`, { autoClose: false });
      }
    }
  };
  const handleDownloadSynergyProjectList = async () => {
    if (selectedCompany === -1) {
      toast.warning('Please select a company first.');
      return;
    }

    setSynergyProjectListLoading(true);
    try {
      const res = await ManageReportDataApi.getSynergyProjectList(selectedCompany);
      if (!res || res.status !== 200 || !res.data?.archiveBase64) {
        throw new Error(res?.message || 'Unable to run the Synergy transaction filter tests.');
      }

      const binary = window.atob(res.data.archiveBase64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i);
      }

      const blob = new Blob([bytes], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = res.data.archiveFilename || 'synergy-transaction-filter-tests.zip';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      const failedTests = (res.data.summaries || []).filter((test) => test.error).length;
      if (failedTests > 0) {
        toast.warning(`Downloaded ${res.data.testCount ?? 0} Synergy transaction tests. ${failedTests} test(s) returned an API error; their JSON files contain the details.`);
      } else {
        toast.success(`Downloaded ${res.data.testCount ?? 0} Synergy transaction filter tests.`);
      }
    } catch (error) {
      toast.error(`Synergy transaction filter test download failed: ${error.message}`);
    } finally {
      setSynergyProjectListLoading(false);
    }
  };

  const collectRawSynergyExport = (res, context, collector) => {
    if (res?.raw_synergy_export_ref) {
      collector.push(res.raw_synergy_export_ref);
    }
    if (res?.raw_synergy_export_error) {
      toast.error(`Synergy raw export could not be created: ${res.raw_synergy_export_error}`, { autoClose: false });
    }
  };

  const shouldCollectRawSynergyExport = (apiType) => (
    downloadRawSynergyExportEnabled && apiType === 'Synergy'
  );

  const buildRawSynergyOptions = (apiType) => (
    shouldCollectRawSynergyExport(apiType) ? { includeRawSynergyExport: true } : {}
  );

  const verifyXeroConnection = async () => {
    try {
      const res = await ManageReportDataApi.checkXeroConnection(selectedCompany);
      if (res?.status === 200 && res?.connected === true) return true;

      toast.error(
        res?.message || 'R4D cannot reach Xero. Please reconnect Xero in Company Settings and try the refresh again.',
        { autoClose: false },
      );
      return false;
    } catch (error) {
      toast.error(
        error?.message || 'R4D cannot reach Xero. Please reconnect Xero in Company Settings and try the refresh again.',
        { autoClose: false },
      );
      return false;
    }
  };

  useEffect(() => {
    const asyncFn = async () => {
      let comps = [], orgs = [], datesArr = [], projectsArr = [], projectForecastsArr = [], clientsArr = [], projecttypesArr = [], projectsubtypesArr = [], employeesArr = [], coaArr = [];
      await Promise.all([
        ManageReportDataApi.getCompanies()
          .then((res) => { if (res.status === 200) comps = res.data; else toast.error(res.message); })
          .catch((error) => toast.error(error.message)),
        ManageReportDataApi.getOrganizations()
          .then((res) => { if (res.status === 200) orgs = res.data; else toast.error(res.message); })
          .catch((error) => toast.error(error.message)),
        ManageReportDataApi.getDates()
          .then((res) => { if (res.status === 200) datesArr = res.data; else toast.error(res.message); })
          .catch((error) => toast.error(error.message)),
        ManageReportDataApi.getEmployees()
          .then((res) => { if (res.status === 200) employeesArr = res.data; else toast.error(res.message); })
          .catch((error) => toast.error(error.message)),
        ManageReportDataApi.getClients()
          .then((res) => { if (res.status === 200) clientsArr = res.data; else toast.error(res.message); })
          .catch((error) => toast.error(error.message)),
        ManageReportDataApi.getProjects()
          .then((res) => { if (res.status === 200) projectsArr = res.data; else toast.error(res.message); })
          .catch((error) => toast.error(error.message)),
        ManageReportDataApi.getTemplate('Project Forecast')
          .then((res) => { if (res.status === 200) projectForecastsArr = res.data; else toast.error(res.message); })
          .catch((error) => toast.error(error.message)),
        ManageReportDataApi.getProjectTypes()
          .then((res) => { if (res.status === 200) projecttypesArr = res.data; else toast.error(res.message); })
          .catch((error) => toast.error(error.message)),
        ManageReportDataApi.getProjectSubTypes()
          .then((res) => { if (res.status === 200) projectsubtypesArr = res.data; else toast.error(res.message); })
          .catch((error) => toast.error(error.message)),
        ManageReportDataApi.getChartOfAccouts()
          .then((res) => { if (res.status === 200) coaArr = res.data; else toast.error(res.message); })
          .catch((error) => toast.error(error.message)),
        ManageReportDataApi.getSelectedAccountingApi().then((res) => {
          if (res && res.status === 200) {
            const { filterTableNames, tableComp_names, summaryFlag, forecastFlag } = buildTableNamesAndComps(res.data);
            setFilterTableNames(filterTableNames);
            setTableCompanyNames(tableComp_names);
            setSelectedApis(res.data);
            setProjectPerformanceSummaryFlag(summaryFlag);
            setEmployeeForecastFlag(forecastFlag);
          } else if (res) toast.error(res.message);
        }).catch((error) => toast.error(error.message)),
      ]);
      const company_org_id = appdata.getItem('company_org_id');
      let selectedCompanyId = company_org_id ? company_org_id : comps?.[0]?.id ?? -1;
      let selectedOrganisationId = orgs.find((org) => org.DimCompanyKey === selectedCompanyId)?.id ?? -1;
      let selectedDatesArr = new Date().getMonth() === 0
        ? [parseInt((new Date().getFullYear() - 1).toString() + '1201')]
        : [parseInt(new Date().getFullYear().toString() + ('0' + new Date().getMonth().toString()).slice(-2) + '01')];
      // Hide General Ledger Expense row
      let cells = document.getElementsByClassName('MuiTableCell-root');
      for (let i = 0; i < cells.length; i++) {
        if (cells[i].innerHTML === 'General Ledger Expense') {
          cells[i].parentElement.style.display = 'none';
        }
      }
      setCompanies(comps);
      setOrganisations(orgs);
      setDates(datesArr);
      setSelectedCompany(selectedCompanyId);
      setSelectedOrganisation(selectedOrganisationId);
      setSelectedDates(selectedDatesArr);
      setEmployees(employeesArr.sort((a, b) => (a[1] < b[1] ? -1 : 1)));
      setClients(clientsArr.sort((a, b) => (a[1] < b[1] ? -1 : 1)));
      setProjects(projectsArr.sort((a, b) => (a[1] < b[1] ? -1 : 1)));
      setProjectForecasts(projectForecastsArr);
      setProjecttypes(projecttypesArr.sort((a, b) => (a[1] < b[1] ? -1 : 1)));
      setProjectsubtypes(projectsubtypesArr.sort((a, b) => (a[1] < b[1] ? -1 : 1)));
      setChartofaccounts(coaArr);
    };
    asyncFn();
  }, []);

  const resetReportingState = () => {
    dispatch(DefaultActions.defaultSetCompany(null));
    dispatch(DefaultActions.defaultSetDimDate(null));
  };

  const getClients = () => {
    ManageReportDataApi.getClients().then((res) => {
      if (res.status === 200) setClients(res.data.sort((a, b) => (a[1] < b[1] ? -1 : 1)));
      else toast.error(res.message);
    }).catch((error) => toast.error(error.message));
  };

  const getEmployees = () => {
    ManageReportDataApi.getEmployees().then((res) => {
      if (res.status === 200) setEmployees(res.data.sort((a, b) => (a[1] < b[1] ? -1 : 1)));
      else toast.error(res.message);
    }).catch((error) => toast.error(error.message));
  };

  const getProjects = () => {
    ManageReportDataApi.getProjects().then((res) => {
      if (res.status === 200) setProjects(res.data.sort((a, b) => (a[1] < b[1] ? -1 : 1)));
      else toast.error(res.message);
    }).catch((error) => toast.error(error.message));
  };

  const getProjectTypes = () => {
    ManageReportDataApi.getProjectTypes().then((res) => {
      if (res.status === 200) setProjecttypes(res.data.sort((a, b) => (a[1] < b[1] ? -1 : 1)));
      else toast.error(res.message);
    }).catch((error) => toast.error(error.message));
  };

  const getProjectSubTypes = () => {
    ManageReportDataApi.getProjectSubTypes().then((res) => {
      if (res.status === 200) setProjectsubtypes(res.data.sort((a, b) => (a[1] < b[1] ? -1 : 1)));
      else toast.error(res.message);
    }).catch((error) => toast.error(error.message));
  };
  
  const onUpdateDropDownData = (tableName) => {
    if (tableName === 'General Ledger') {
      ManageReportDataApi.getChartOfAccouts().then((res) => {
        if (res.status === 200) setChartofaccounts(res.data);
        else toast.error(res.message);
      }).catch((error) => toast.error(error.message));
    }
    if (tableName === 'Employee') {
      ManageReportDataApi.getEmployees().then((res) => {
        if (res.status === 200) setEmployees(res.data.sort((a, b) => (a[1] < b[1] ? -1 : 1)));
        else toast.error(res.message);
      }).catch((error) => toast.error(error.message));
    }
    if (tableName === 'Client') {
      ManageReportDataApi.getClients().then((res) => {
        if (res.status === 200) setClients(res.data.sort((a, b) => (a[1] < b[1] ? -1 : 1)));
        else toast.error(res.message);
      }).catch((error) => toast.error(error.message));
    }
    if (tableName === 'Project') {
      ManageReportDataApi.getProjects().then((res) => {
        if (res.status === 200) setProjects(res.data.sort((a, b) => (a[1] < b[1] ? -1 : 1)));
        else toast.error(res.message);
      }).catch((error) => toast.error(error.message));
    }
  };

  //const getStateData = (data) => setTabledata(data);

  const getStateData = (data) => {
  // Support both:
  // 1) new shape: { data: [...rows], new_records: number }
  // 2) old shape: [...rows]
  if (data && Array.isArray(data.data)) {
    setTabledata(data.data);
  } else if (Array.isArray(data)) {
    setTabledata(data);
  } else {
    setTabledata([]);
  }
};

  const groupRoleCheck = (name, value) => {
    if (name === 'groupCheck') setGroupCheck(value);
    else setRoleCheck(value);
  };

  const companyOrgs = useMemo(() => {
    return organisations.filter((org) => selectedCompany === org.DimCompanyKey && org.IsActive);
  }, [organisations, selectedCompany]);

  // employeePrimary now includes DimOrganisationKey at tuple index 6. Keep
  // employee pickers scoped to the organisation selected at the top of Data
  // Management so staff from other organisations cannot leak into dropdowns.
  // The metadata guard keeps this backward compatible during a rolling deploy.
  const scopedEmployees = useMemo(() => {
    if (!Array.isArray(employees) || selectedOrganisation === -1) return employees || [];
    const hasOrganisationMetadata = employees.some(
      (employee) => Array.isArray(employee) && employee.length > 6 && employee[6] != null,
    );
    if (!hasOrganisationMetadata) return employees;

    const organisationId = Number(selectedOrganisation);
    return employees.filter((employee) => Number(employee?.[6]) === organisationId);
  }, [employees, selectedOrganisation]);

  const updateTemplate = (tableName) => {
    ManageReportDataApi.getTemplate(tableName)
      .then((res) => {
        if (res.status === 200) {
          getStateData(res.data);
          onUpdateDropDownData(tableName);
        } else toast.error(res.message);
      })
      .catch((error) => toast.error(error.message));
  };

  const refreshTemplate = async (tableName, apiType) => {
    if (selectedTableName !== tableName) return;

    if (apiType === 'Xero') {
      setRefreshLoading(true);
      const xeroConnected = await verifyXeroConnection();
      if (!xeroConnected) {
        setRefreshLoading(false);
        return;
      }
    }

    const rawSynergyExports = [];
    setRefreshLoading(true);

    if (apiType !== 'Deltek') {
      for (let i = 0; i < selectedDates.length; i++) {
        let res = null;
        if (tableName === 'Employee') {
          res = await ManageReportDataApi.refreshTemplate(
            tableName,
            apiType,
            selectedCompany,
            selectedOrganisation,
            selectedDates[i],
            roleCheck,
            groupCheck,
            buildRawSynergyOptions(apiType),
          );
        } else {
          res = await ManageReportDataApi.refreshTemplate(
            tableName,
            apiType,
            selectedCompany,
            selectedOrganisation,
            selectedDates[i],
            undefined,
            undefined,
            buildRawSynergyOptions(apiType),
          );
        }

        if (res.status === 200) {
          collectRawSynergyExport(res, {
            source: 'single-refresh',
            tableName,
            apiType,
            selectedDate: selectedDates[i],
          }, rawSynergyExports);

          if (tableName === 'General Ledger' || tableName === 'Client') {
            updateTemplate(tableName);
            onUpdateDropDownData(tableName);
            break;
          }

          if (i === selectedDates.length - 1) {
            updateTemplate(tableName);
            onUpdateDropDownData(tableName);
          }
        } else {
          toast.error(tableName + ' : ' + res.message);
          break;
        }
      }
    } else {
      for (let i = 0; i < selectedDates.length; i++) {
        let res = null;
        if (tableName === 'Employee Forecast') {
          res = await ManageReportDataApi.refreshTemplate(
            tableName,
            apiType,
            selectedCompany,
            selectedOrganisation,
            selectedDates[i],
            undefined,
            undefined,
            buildRawSynergyOptions(apiType),
          );

          if (res.status === 200) {
            collectRawSynergyExport(res, {
              source: 'single-refresh',
              tableName,
              apiType,
              selectedDate: selectedDates[i],
            }, rawSynergyExports);

            if (tableName === 'Client') {
              updateTemplate(tableName);
              onUpdateDropDownData(tableName);
              break;
            }

            if (i === selectedDates.length - 1) {
              updateTemplate(tableName);
              onUpdateDropDownData(tableName);
            }
          } else {
            toast.error(tableName + ' : ' + res.message);
            break;
          }
        } else {
          await ManageReportDataApi.refreshDeltekTemplate(
            tableName, selectedCompany, selectedOrganisation, selectedDates[i], roleCheck, groupCheck
          )
          .then((res) => {
            if (res.status === 200) {
              if (i === selectedDates.length - 1) {
                updateTemplate(tableName);
                onUpdateDropDownData(tableName);
              }
            } else {
              toast.error(tableName + ' : ' + res.message);
            }
          })
          .catch((error) => {
            toast.error(tableName + ' : ' + error.message);
          });
        }
      }
    }

    setRefreshLoading(false);
    if (downloadRawSynergyExportEnabled) {
      downloadRawSynergyExport(rawSynergyExports, `${tableName}-${apiType || 'manual'}-refresh`);
    }
  };

  const onAddTemplate = (tableName, type = 'manual') => {
    if (selectedOrganisation === -1) {
      toast.warning('Please select the organisation!');
      return;
    }
    if (type === 'auto') {
      ManageReportDataApi.addAutoTemplate(tableName, selectedCompany, selectedOrganisation, selectedDates[0])
        .then((res) => {
          if (res.status === 200) updateTemplate(tableName);
          else toast.error(res.message);
        })
        .catch((error) => toast.error(error.message));
    } else {
      ManageReportDataApi.addTemplate(tableName, selectedCompany, selectedOrganisation, selectedDates[0])
        .then((res) => {
          if (res.status === 200) updateTemplate(tableName);
          else toast.error(res.message);
        })
        .catch((error) => toast.error(error.message));
    }
  };

  const onDeleteTemplate = async (tableName, rowIndex) => {
    ManageReportDataApi.deleteTemplate(tableName, rowIndex)
      .then((delRes) => {
        if (delRes.status === 200) updateTemplate(tableName);
        else {
          if (delRes.message) toast.error(delRes.message);
          else {
            if (delRes.employee) {
              let idString = JSON.stringify(delRes.employee).slice(1).slice(0, -1);
              toast.warning(`We can't delete follow ids. Please check the related data in the FactEmployeeUtilisation, FactAccountsReceivableInvoice, FactProjectPerformanceSummary, and FactWorkGenerated tables. ids: ${idString}`);
            } else if (delRes.project) {
              let idString = JSON.stringify(delRes.project).slice(1).slice(0, -1);
              toast.warning(`We can't delete follow ids. Please check the related data in the FactProjectPerformanceSummary, FactAccountsReceivableInvoice, DimProjectForecast, and FactWorkGenerated tables. ids: ${idString}`);
            }
          }
        }
      })
      .catch((error) => toast.error(error.message));
  };

  const handleSelectCompany = async (ev) => {
    appdata.setItem('company_org_id', ev.target.value);
    setSelectedCompany(ev.target.value);

    let projectForecastsArr = [], clientsArr = [], employeesArr = [], coaArr = [];
    await Promise.all([
      ManageReportDataApi.getEmployees()
        .then((res) => { if (res.status === 200) employeesArr = res.data; else toast.error(res.message); })
        .catch((error) => toast.error(error.message)),
      ManageReportDataApi.getClients()
        .then((res) => { if (res.status === 200) clientsArr = res.data; else toast.error(res.message); })
        .catch((error) => toast.error(error.message)),
      ManageReportDataApi.getTemplate('Project Forecast')
        .then((res) => { if (res.status === 200) projectForecastsArr = res.data; else toast.error(res.message); })
        .catch((error) => toast.error(error.message)),
      ManageReportDataApi.getChartOfAccouts()
        .then((res) => { if (res.status === 200) coaArr = res.data; else toast.error(res.message); })
        .catch((error) => toast.error(error.message)),
      ManageReportDataApi.getSelectedAccountingApi().then((res) => {
        if (res && res.status === 200) {
          const { filterTableNames, tableComp_names, summaryFlag, forecastFlag } = buildTableNamesAndComps(res.data);
          setFilterTableNames(filterTableNames);
          setTableCompanyNames(tableComp_names);
          setSelectedApis(res.data);
          setProjectPerformanceSummaryFlag(summaryFlag);
          setEmployeeForecastFlag(forecastFlag);

          if (filterTableNames.indexOf(selectedTableName) !== -1) {
            updateTemplate(selectedTableName);
          } else {
            setTabledata([]);
            setSelectedTableName('');
          }
        } else if (res) toast.error(res.message);
      }).catch((error) => toast.error(error.message)),
    ]);
    const company_org_id = ev.target.value;
    let selectedCompanyId = company_org_id ? company_org_id : companies?.[0]?.id ?? -1;
    let selectedOrganisationId = organisations.find((org) => org.DimCompanyKey === selectedCompanyId)?.id ?? -1;

    setSelectedCompany(selectedCompanyId);
    setSelectedOrganisation(selectedOrganisationId);
    setEmployees(employeesArr.sort((a, b) => (a[1] < b[1] ? -1 : 1)));
    setClients(clientsArr.sort((a, b) => (a[1] < b[1] ? -1 : 1)));
    setProjectForecasts(projectForecastsArr);
    setChartofaccounts(coaArr);
  };

  const handleChangeFilter = (event) => setActiveFilter(event.target.checked);

  const handleTemplateSelect = async (tableName) => {
    if (selectedTableName !== tableName) {
      setSelectedTableName(tableName);
      setTabledata([]);
      await updateTemplate(tableName);
    }
  };

  const handleTemplateRefresh = (ev, tableName, apiType = '') => {
    ev.preventDefault();
    if (selectedCompany === -1 || selectedOrganisation === -1 || selectedDates.length === 0) {
      toast.warning('Please fill the CompanyId, OrganisationId and Date');
    } else {
      refreshTemplate(tableName, apiType);
    }
  };
  
  const handleRefreshAll = async () => {
    if (selectedCompany === -1 || selectedOrganisation === -1 || selectedDates.length === 0) {
      toast.warning('Please fill the CompanyId, OrganisationId and Date');
    } else {
      const hasXeroRefresh = selectedApis.some((api) => {
        if (api.ApiType !== 'Xero') return false;
        try {
          const accounts = JSON.parse(api.ApiAccounts || '[]');
          return accounts.some((account) => xeroAccountLists.indexOf(account) !== -1);
        } catch (error) {
          return false;
        }
      });

      if (hasXeroRefresh) {
        setRefreshLoading(true);
        const xeroConnected = await verifyXeroConnection();
        if (!xeroConnected) {
          setRefreshLoading(false);
          setRefreshAllLoading(false);
          setTotalRefreshCount(0);
          setCurrentRefreshCount(0);
          return;
        }
      }

      let refreshTables = [];
      let rawSynergyExports = [];
      let _currentRefreshCount = 0;
      let _totalRefreshCount = 0;
      let nextApiFlag = true;

      selectedApis.forEach((api) => {
        if (JSON.parse(api.ApiAccounts || '[]').length > 0) {
          if (api.ApiType === 'Xero') {
            let apiAccounts = JSON.parse(api.ApiAccounts || '[]');
            apiAccounts.forEach((account) => {
              if (xeroAccountLists.indexOf(account) !== -1) {
                refreshTables.push({
                  apiType: 'Xero',
                  account: account,
                });
                _totalRefreshCount++;
              }
            });
          } else if (api.ApiType === 'Myob') {
            let apiAccounts = JSON.parse(api.ApiAccounts || '[]');
            apiAccounts.forEach((account) => {
              if (myobAccountLists.indexOf(account) !== -1) {
                refreshTables.push({
                  apiType: 'Myob',
                  account: account,
                });
                _totalRefreshCount++;
              }
            });
          } else if (api.ApiType === 'Sql') {
            let apiAccounts = JSON.parse(api.ApiAccounts || '[]');
            apiAccounts.forEach((account) => {
              if (sqlAccountLists.indexOf(account) !== -1) {
                refreshTables.push({
                  apiType: 'Sql',
                  account: account,
                });
                _totalRefreshCount++;
              }
            });
          } else if (api.ApiType === 'Rapport') {
            let apiAccounts = JSON.parse(api.ApiAccounts || '[]');
            apiAccounts.forEach((account) => {
              if (rapportAccountLists.indexOf(account) !== -1) {
                refreshTables.push({
                  apiType: 'Rapport',
                  account: account,
                });
                _totalRefreshCount++;
              }
            });
          } else if (api.ApiType === 'Workflow') {
            let apiAccounts = JSON.parse(api.ApiAccounts || '[]');
            apiAccounts.forEach((account) => {
              if (workflowAccountLists.indexOf(account) !== -1) {
                refreshTables.push({
                  apiType: 'Workflow',
                  account: account,
                });
                _totalRefreshCount++;
              }
            });
          } else if (api.ApiType === 'Manual Entry') {
            let apiAccounts = JSON.parse(api.ApiAccounts || '[]');
            apiAccounts.forEach((account) => {
              if (manaualEntryAccountLists.indexOf(account) !== -1) {
                refreshTables.push({
                  apiType: 'Manual Entry',
                  account: account,
                });
                _totalRefreshCount++;
              }
            });
          } else if (api.ApiType === 'Synergy') {
            let apiAccounts = JSON.parse(api.ApiAccounts || '[]');
            apiAccounts.forEach((account) => {
              if (synergyAccountLists.indexOf(account) !== -1) {
                refreshTables.push({
                  apiType: 'Synergy',
                  account: account,
                });
                _totalRefreshCount++;
              }
            });
          } else if (api.ApiType === 'Deltek') {
            let apiAccounts = JSON.parse(api.ApiAccounts || '[]');
            apiAccounts.forEach((account) => {
              if (deltekVpAccountLists.indexOf(account) !== -1) {
                refreshTables.push({
                  apiType: 'Deltek',
                  account: account,
                });
                _totalRefreshCount++;
              }
            });
          }
          else if (api.ApiType === 'Simplify') {
            let apiAccounts = JSON.parse(api.ApiAccounts || '[]');
            apiAccounts.forEach((account) => {
              if (simplifyAccountLists.indexOf(account) !== -1 && account !== 'Project Forecast') {
                refreshTables.push({
                  apiType: 'Simplify',
                  account: account,
                });
                _totalRefreshCount++;
              }
            });
          }
        }
      });

      // Sort refreshTables according to filterTableNames order
      refreshTables.sort((a, b) => {
        const indexA = filterTableNames.indexOf(a.account);
        const indexB = filterTableNames.indexOf(b.account);
        return indexA - indexB;
      });

      let generalLedgerFlag = refreshTables.find(table => table.account === 'General Ledger' && table.apiType === 'Deltek');
      let clientFlag = refreshTables.find(table => table.account === 'Client');

      if (generalLedgerFlag) {
        _totalRefreshCount--;
      }
      if (clientFlag) {
        _totalRefreshCount--;
      }

      _totalRefreshCount = _totalRefreshCount * selectedDates.length;

      if (generalLedgerFlag) {
        _totalRefreshCount++;
      }
      if (clientFlag) {
        _totalRefreshCount++;
      }

      setTotalRefreshCount(_totalRefreshCount);
      setRefreshAllLoading(true);
      setRefreshLoading(true);

      for (const { apiType, account } of refreshTables) {
        if (nextApiFlag) {
          if (apiType === 'Deltek' && account === 'General Ledger') {
            let res = await ManageReportDataApi.refreshDeltekTemplate(
              account, selectedCompany, selectedOrganisation, selectedDates[0], roleCheck, groupCheck
            )
            
            if (res.status === 200) {
              if (res.add > 0) {  
                setNewRecordsConfirmModal({
                  open: true,
                  tableName: account,
                  newRecordsCount: res.add,
                });
                
                // Wait for user decision before continuing the loop
                await new Promise((resolve) => {
                  const checkDecision = () => {
                    if (refreshContinueRef.current !== null) {
                      resolve();
                    } else {
                      setTimeout(checkDecision, 100);
                    }
                  };
                  checkDecision();
                });
                
                // If user cancelled, break out of all loops
                if (refreshContinueRef.current === false) {
                  return; // This will exit the entire function
                }
                
                // Reset the ref for next potential new records
                refreshContinueRef.current = null;
              }
            } else {
              toast.error(res.message);
              nextApiFlag = false;
            }

            handleTemplateSelect(account);

            // Check if user cancelled from previous modal
            if (refreshContinueRef.current === false) {
              return; // This will exit the entire function
            }

            _currentRefreshCount++;
            setCurrentRefreshCount(_currentRefreshCount);

            continue;
          }

          if (account === 'Client') {
            if (apiType === 'Deltek') {
              let res = await ManageReportDataApi.refreshDeltekTemplate(
                account, selectedCompany, selectedOrganisation, selectedDates[0], roleCheck, groupCheck
              )
              
              if (res.status === 200) {
                collectRawSynergyExport(res, {
                  source: 'refresh-all',
                  tableName: account,
                  apiType,
                  selectedDate: selectedDates[0],
                }, rawSynergyExports);

                if (res.add > 0) {  
                  setNewRecordsConfirmModal({
                    open: true,
                    tableName: account,
                    newRecordsCount: res.add,
                  });
                  
                  // Wait for user decision before continuing the loop
                  await new Promise((resolve) => {
                    const checkDecision = () => {
                      if (refreshContinueRef.current !== null) {
                        resolve();
                      } else {
                        setTimeout(checkDecision, 100);
                      }
                    };
                    checkDecision();
                  });
                  
                  // If user cancelled, break out of all loops
                  if (refreshContinueRef.current === false) {
                    return; // This will exit the entire function
                  }
                  
                  // Reset the ref for next potential new records
                  refreshContinueRef.current = null;
                }
              } else {
                toast.error(res.message);
                nextApiFlag = false;
              }
            } else {
              let res = await ManageReportDataApi.refreshTemplate(
                account,
                apiType,
                selectedCompany,
                selectedOrganisation,
                selectedDates[0],
                undefined,
                undefined,
                buildRawSynergyOptions(apiType),
              );

              if (res.status === 200) {
                collectRawSynergyExport(res, {
                  source: 'refresh-all',
                  tableName: account,
                  apiType,
                  selectedDate: selectedDates[0],
                }, rawSynergyExports);

                if (res.add > 0) {
                  setNewRecordsConfirmModal({
                    open: true,
                    tableName: account,
                    newRecordsCount: res.add,
                  });
                  
                  // Wait for user decision before continuing the loop
                  await new Promise((resolve) => {
                    const checkDecision = () => {
                      if (refreshContinueRef.current !== null) {
                        resolve();
                      } else {
                        setTimeout(checkDecision, 100);
                      }
                    };
                    checkDecision();
                  });
                  
                  // If user cancelled, break out of all loops
                  if (refreshContinueRef.current === false) {
                    return; // This will exit the entire function
                  }
                  
                  // Reset the ref for next potential new records
                  refreshContinueRef.current = null;
                }
              } else {
                toast.error(res.message);
                nextApiFlag = false;
              }
            }

            handleTemplateSelect(account);

            // Check if user cancelled from previous modal
            if (refreshContinueRef.current === false) {
              return; // This will exit the entire function
            }

            _currentRefreshCount++;
            setCurrentRefreshCount(_currentRefreshCount);

            continue;
          }

          for (let i = 0; i < selectedDates.length; i++) {
            if (apiType !== 'Deltek') {
              let res = account === 'Employee' ? await ManageReportDataApi.refreshTemplate(
                account,
                apiType,
                selectedCompany,
                selectedOrganisation,
                selectedDates[i],
                roleCheck,
                groupCheck,
                buildRawSynergyOptions(apiType),
              ) : await ManageReportDataApi.refreshTemplate(
                account,
                apiType,
                selectedCompany,
                selectedOrganisation,
                selectedDates[i],
                undefined,
                undefined,
                buildRawSynergyOptions(apiType),
              );

              if (res.status === 200) {
                collectRawSynergyExport(res, {
                  source: 'refresh-all',
                  tableName: account,
                  apiType,
                  selectedDate: selectedDates[i],
                }, rawSynergyExports);

                if (res.add > 0) {
                  setNewRecordsConfirmModal({
                    open: true,
                    tableName: account,
                    newRecordsCount: res.add,
                  });
                  
                  // Wait for user decision before continuing the loop
                  await new Promise((resolve) => {
                    const checkDecision = () => {
                      if (refreshContinueRef.current !== null) {
                        resolve();
                      } else {
                        setTimeout(checkDecision, 100);
                      }
                    };
                    checkDecision();
                  });
                  
                  // If user cancelled, break out of all loops
                  if (refreshContinueRef.current === false) {
                    return; // This will exit the entire function
                  }
                  
                  // Reset the ref for next potential new records
                  refreshContinueRef.current = null;
                }
              } else {
                toast.error(res.message);
                nextApiFlag = false;
              }
            } else {
              if (account === 'Employee Forecast') {
                let res = await ManageReportDataApi.refreshTemplate(
                  account,
                  apiType,
                  selectedCompany,
                  selectedOrganisation,
                  selectedDates[i],
                  undefined,
                  undefined,
                  buildRawSynergyOptions(apiType),
                );
                
                if (res.status === 200) {
                  collectRawSynergyExport(res, {
                    source: 'refresh-all',
                    tableName: account,
                    apiType,
                    selectedDate: selectedDates[i],
                  }, rawSynergyExports);

                  if (res.add > 0) {  
                    setNewRecordsConfirmModal({
                      open: true,
                      tableName: account,
                      newRecordsCount: res.add,
                    });
                    
                    // Wait for user decision before continuing the loop
                    await new Promise((resolve) => {
                      const checkDecision = () => {
                        if (refreshContinueRef.current !== null) {
                          resolve();
                        } else {
                          setTimeout(checkDecision, 100);
                        }
                      };
                      checkDecision();
                    });
                    
                    // If user cancelled, break out of all loops
                    if (refreshContinueRef.current === false) {
                      return; // This will exit the entire function
                    }
                    
                    // Reset the ref for next potential new records
                    refreshContinueRef.current = null;
                  }
                } else {
                  toast.error(res.message);
                  nextApiFlag = false;
                }
              } else {
                let res = await ManageReportDataApi.refreshDeltekTemplate(
                  account, selectedCompany, selectedOrganisation, selectedDates[i], roleCheck, groupCheck
                )
                
                if (res.status === 200) {
                  if (res.add > 0) {  
                    setNewRecordsConfirmModal({
                      open: true,
                      tableName: account,
                      newRecordsCount: res.add,
                    });
                    
                    // Wait for user decision before continuing the loop
                    await new Promise((resolve) => {
                      const checkDecision = () => {
                        if (refreshContinueRef.current !== null) {
                          resolve();
                        } else {
                          setTimeout(checkDecision, 100);
                        }
                      };
                      checkDecision();
                    });
                    
                    // If user cancelled, break out of all loops
                    if (refreshContinueRef.current === false) {
                      return; // This will exit the entire function
                    }
                    
                    // Reset the ref for next potential new records
                    refreshContinueRef.current = null;
                  }
                } else {
                  toast.error(res.message);
                  nextApiFlag = false;
                }
              }
            }

            handleTemplateSelect(account);

            // Check if user cancelled from previous modal
            if (refreshContinueRef.current === false) {
              return; // This will exit the entire function
            }

            _currentRefreshCount++;
            setCurrentRefreshCount(_currentRefreshCount);
          }
        }
      }
      
      setRefreshLoading(false);
      setRefreshAllLoading(false);
      if (downloadRawSynergyExportEnabled) {
        downloadRawSynergyExport(rawSynergyExports, 'refresh-all');
      }
    }
  };

  const handleNewRecordsContinue = () => {
    setNewRecordsConfirmModal({
      open: false,
      tableName: '',
      newRecordsCount: 0,
    });
    refreshContinueRef.current = true;
  };

  const handleNewRecordsCancel = () => {
    setNewRecordsConfirmModal({
      open: false,
      tableName: '',
      newRecordsCount: 0,
    });
    refreshContinueRef.current = false;
    setRefreshAllLoading(false);
    setTotalRefreshCount(0);
    setCurrentRefreshCount(0);
    setRefreshLoading(false);
  };
  
  return (
    <div className="ManageReportData">
      {refreshLoading && (
        <CircularProgress
          size={68}
          sx={{ color: '#4caf50', position: 'absolute', top: '45%', left: '50%', zIndex: 9999 }}
        />
      )}
      <div style={{ display: 'flex', height: 'calc(100% - 40px)' }}>
        <Box sx={{ width: 380, minWidth: 380 }}>
          <Box sx={{ width: 374 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                sx={{ flex: '0 0 170px' }}
                variant="outlined"
                color="secondary"
                onClick={handleRefreshAll}
                endIcon={<RefreshIcon />}
                loading={refreshAllLoading}
                loadingPosition="end"
              >
                Refresh All
              </Button>
              {hasSynergyIntegration && (
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={downloadRawSynergyExportEnabled}
                      onChange={(ev) => setDownloadRawSynergyExportEnabled(ev.target.checked)}
                      color="success"
                      size="small"
                    />
                  )}
                  label="Download Synergy raw data"
                  sx={{
                    m: 0,
                    '& .MuiFormControlLabel-label': { fontSize: 12, lineHeight: 1.15 },
                  }}
                />
              )}
            </Box>
            {hasSynergyIntegration && (
              <Button
                sx={{ mt: 0.75, width: 374 }}
                size="small"
                variant="outlined"
                color="secondary"
                onClick={handleDownloadSynergyProjectList}
                endIcon={<DownloadIcon />}
                disabled={synergyProjectListLoading}
              >
                {synergyProjectListLoading ? 'Running Synergy transaction tests...' : 'Download Synergy transaction filter tests'}
              </Button>
            )}
          </Box>
          {refreshAllLoading && (
            <LinearProgressWithLabel value={Math.round(currentRefreshCount / totalRefreshCount * 100)} />
          )}
          <Paper style={{ marginTop: 8, marginRight: 8, height: 'calc(100vh - 150px)', overflow: 'auto' }}>
            <Table aria-label="table">
              <TableHead>
                <TableRow>
                  <TableCell align="left">Name</TableCell>
                  <TableCell align="left"></TableCell>
                  <TableCell align="left"></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filterTableNames.map((tableName, index) => (
                  <React.Fragment key={index}>
                    <ApiTableRow
                      tableName={tableName}
                      selectedApis={selectedApis}
                      selectedTableName={selectedTableName}
                      refreshLoading={refreshLoading}
                      handleTemplateSelect={handleTemplateSelect}
                      handleTemplateRefresh={handleTemplateRefresh}
                    />
                    {projectPerformanceSummaryFlag && tableName === 'Project Performance Summary' && (
                      <TableRow style={{ height: 77 }}>
                        <TableCell component="td" scope="row" align="left" colSpan={3} sx={{ cursor: 'pointer' }} onClick={() => handleTemplateSelect(tableName)}>
                          Project Performance Summary
                        </TableCell>
                      </TableRow>
                    )}
                    {employeeForecastFlag && tableName === 'Employee Forecast' && (
                      <TableRow sx={{ background: selectedTableName === 'Employee Forecast' ? 'rgba(0,0,0,0.1)' : '' }} style={{ height: 77 }}>
                        <TableCell component="td" scope="row" align="left" sx={{ cursor: 'pointer' }} onClick={() => handleTemplateSelect(tableName)}>
                          Employee Forecast
                        </TableCell>
                        <TableCell align="right" sx={{ width: 137, paddingRight: '2px !important', paddingLeft: '2px !important' }}>
                          <ProgressButton
                            variant="contained"
                            color="secondary"
                            size="small"
                            onClick={(ev) => handleTemplateRefresh(ev, tableName)}
                            loading={selectedTableName === tableName && refreshLoading}
                            style={{ marginTop: 0 }}
                          >
                            Refresh
                          </ProgressButton>
                        </TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
                {/* Financial Metric row */}
                <TableRow sx={{ background: selectedTableName === 'Financial Metric' ? 'rgba(0,0,0,0.1)' : '' }} style={{ height: 77 }}>
                  <TableCell component="td" scope="row" align="left" sx={{ cursor: 'pointer' }} onClick={() => handleTemplateSelect('Financial Metric')}>
                    Financial Metric
                  </TableCell>
                  <TableCell align="right" sx={{ width: 137, paddingRight: '2px !important', paddingLeft: '2px !important' }}>
                    <ProgressButton
                      variant="contained"
                      color="secondary"
                      size="small"
                      onClick={(ev) => handleTemplateRefresh(ev, 'Financial Metric')}
                      loading={selectedTableName === 'Financial Metric' && refreshLoading}
                      style={{ marginTop: 0 }}
                    >
                      Refresh
                    </ProgressButton>
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
                {/* KPI Result row */}
                <TableRow sx={{ background: selectedTableName === 'KPI Result' ? 'rgba(0,0,0,0.1)' : '' }} style={{ height: 77 }}>
                  <TableCell component="td" scope="row" align="left" sx={{ cursor: 'pointer' }} onClick={() => handleTemplateSelect('KPI Result')}>
                    KPI Result
                  </TableCell>
                  <TableCell align="right" sx={{ width: 137, paddingRight: '2px !important', paddingLeft: '2px !important' }}>
                    <ProgressButton
                      variant="contained"
                      color="secondary"
                      size="small"
                      onClick={(ev) => handleTemplateRefresh(ev, 'KPI Result')}
                      loading={selectedTableName === 'KPI Result' && refreshLoading}
                      style={{ marginTop: 0 }}
                    >
                      Refresh
                    </ProgressButton>
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Paper>
        </Box>
        <Box sx={{ width: 'calc(100% - 380px)', height: 'calc(100vh - 150px)' }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <label style={{ lineHeight: 1 }}>Company:</label>
            <FormControl variant="standard" sx={{ minWidth: 100 }}>
              <Select value={selectedCompany} onChange={handleSelectCompany}>
                <MenuItem value={-1} disabled> </MenuItem>
                {companies && companies.map((cur) => cur.IsActive && (
                  <MenuItem key={cur.id} value={cur.id}>{cur.CompanyName}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <label style={{ lineHeight: 1 }}>Organisation:</label>
            <FormControl variant="standard" sx={{ minWidth: 80 }}>
              <Select value={selectedOrganisation} onChange={(ev) => setSelectedOrganisation(ev.target.value)}>
                <MenuItem value={-1} disabled> </MenuItem>
                {organisations && organisations.map((cur) => selectedCompany === cur.DimCompanyKey && cur.IsActive && (
                  <MenuItem key={cur.id} value={cur.id}>{cur.OrgName}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <label style={{ lineHeight: 1 }}>Dates:</label>
            <FormControl variant="standard" sx={{ minWidth: 140 }}>
              <Select
                multiple
                value={selectedDates}
                onChange={(ev) => {
                  if (ev.target.value.length === 0) {
                    setSelectedDates(
                      new Date().getMonth() === 0
                        ? [parseInt((new Date().getFullYear() - 1).toString() + '1201')]
                        : [parseInt(new Date().getFullYear().toString() + ('0' + new Date().getMonth().toString()).slice(-2) + '01')]
                    );
                  } else {
                    setSelectedDates(ev.target.value.sort((a, b) => (a < b ? -1 : 1)));
                  }
                }}
                renderValue={(selected) => {
                  let value = '';
                  selected.forEach((date, index) => {
                    let selectedDate = dates.find((d) => d[0] === date);
                    if (selectedDate) {
                      value += selectedDate[2] + ' ' + selectedDate[3];
                    }
                    
                    if (index !== selected.length - 1) value += ', ';
                  });
                  return value;
                }}
                style={{ width: 140 }}
              >
                {dates && dates.map((cur) => (
                  <MenuItem key={cur[0]} value={cur[0]}>
                    <Checkbox checked={selectedDates.indexOf(cur[0]) > -1} color="success" />
                    {cur[2] + ' ' + cur[3]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {selectedTableName === 'Employee' && (
              <FormControlLabel
                control={<Switch checked={activeFilter} onChange={handleChangeFilter} name="activeFilter" />}
                label="Current Year Only"
                sx={{ ml: 0, mr: 0, '& .MuiFormControlLabel-label': { textAlign: 'center' } }}
              />
            )}
          </Stack>
          {filterTableNames.map((tableName, index) => {
            let TagName = tableCompanyNames[index];
            return (
              tableName === selectedTableName && (
                <TagName
                  key={index}
                  name={tableName}
                  rows={tabledata}
                  companies={companies}
                  selectedCompany={selectedCompany}
                  selectedOrganisation={selectedOrganisation}
                  organisations={organisations}
                  dates={dates}
                  activeFilter={activeFilter}
                  projects={projects}
                  projectForecasts={projectForecasts}
                  clients={clients}
                  projecttypes={projecttypes}
                  projectsubtypes={projectsubtypes}
                  employees={scopedEmployees}
                  chartofaccounts={chartofaccounts}
                  selectedDates={selectedDates}
                  selectedApis={selectedApis}
                  orgLength={companyOrgs}
                  onClickedAdd={onAddTemplate}
                  onClickedUpdate={updateTemplate}
                  onClickedDelete={onDeleteTemplate}
                  getStateData={getStateData}
                  getClients={getClients}
                  getEmployees={getEmployees}
                  getProjects={getProjects}
                  getProjectTypes={getProjectTypes}
                  getProjectSubTypes={getProjectSubTypes}
                  onUpdateDropDownData={onUpdateDropDownData}
                  groupRoleCheck={groupRoleCheck}
                  resetReportingState={resetReportingState}
                  groupCheck={groupCheck}
                  roleCheck={roleCheck}
                />
              )
            );
          })}
          {selectedTableName === 'Financial Metric' && (
            <FinancialMetricTable
              name="Financial Metric"
              rows={tabledata}
              companies={companies}
              organisations={organisations}
              dates={dates}
              activeFilter={activeFilter}
              selectedCompany={selectedCompany}
              selectedDates={selectedDates}
              orgLength={companyOrgs}
              onClickedAdd={onAddTemplate}
              onClickedDelete={onDeleteTemplate}
              getStateData={getStateData}
              resetReportingState={resetReportingState}
            />
          )}
          {selectedTableName === 'KPI Result' && (
            <KPIResultTable
              name="KPI Result"
              rows={tabledata}
              companies={companies}
              organisations={organisations}
              dates={dates}
              activeFilter={activeFilter}
              selectedCompany={selectedCompany}
              selectedDates={selectedDates}
              orgLength={companyOrgs}
              onClickedAdd={onAddTemplate}
              onClickedDelete={onDeleteTemplate}
              getStateData={getStateData}
              resetReportingState={resetReportingState}
            />
          )}
        </Box>
      </div>

      <NewRecordsConfirmModal
        open={newRecordsConfirmModal.open}
        onContinue={handleNewRecordsContinue}
        onCancel={handleNewRecordsCancel}
        tableName={newRecordsConfirmModal.tableName}
        newRecordsCount={newRecordsConfirmModal.newRecordsCount}
      />
    </div>
  );
}

export default ManageReportData;
